// MaintenanceForm — open/edit a maintenance work order
import { useState, useEffect } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["plumbing", "electrical", "hvac", "appliance", "structural", "cosmetic", "landscaping", "pest", "other"];
const PRIORITIES = [
  { value: "low", label: "Low", color: "text-slate-600" },
  { value: "medium", label: "Medium", color: "text-amber-600" },
  { value: "high", label: "High", color: "text-orange-600" },
  { value: "emergency", label: "Emergency", color: "text-red-600" },
];

const EMPTY = {
  title: "",
  description: "",
  category: "plumbing",
  priority: "medium",
  estimated_cost: "",
  actual_cost: "",
  vendor_name: "",
  vendor_phone: "",
  vendor_email: "",
  scheduled_for: "",
  notes: "",
};

export default function MaintenanceForm({ open, onOpenChange, propertyId, item, leaseId, onSaved }) {
  const editing = !!item;
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFiles([]);
      setForm(item ? { ...EMPTY, ...item, estimated_cost: item.estimated_cost ?? "", actual_cost: item.actual_cost ?? "" } : EMPTY);
    }
  }, [open, item]);

  const handle = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 5);
    setFiles(list);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        property_id: propertyId,
        lease_id: leaseId || null,
        title: form.title,
        description: form.description || "",
        category: form.category,
        priority: form.priority,
        estimated_cost: form.estimated_cost ? Number(form.estimated_cost) : null,
        actual_cost: form.actual_cost ? Number(form.actual_cost) : null,
        vendor_name: form.vendor_name || null,
        vendor_phone: form.vendor_phone || null,
        vendor_email: form.vendor_email || null,
        scheduled_for: form.scheduled_for || null,
        notes: form.notes || null,
      };
      let savedId;
      if (editing) {
        await api.patch(`/manage/maintenance/${item.id}`, payload);
        savedId = item.id;
      } else {
        const { data } = await api.post("/manage/maintenance", payload);
        savedId = data.id;
      }

      if (files.length > 0 && savedId) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/manage/maintenance/${savedId}/photos`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(editing ? "Updated" : "Logged");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-slate-200 bg-white" data-testid="maintenance-form-modal">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit maintenance" : "Log maintenance"}</DialogTitle>
          <DialogDescription className="text-slate-500">
            Track repairs and vendor work. Add up to 5 photos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="m-title">Title *</Label>
            <Input id="m-title" data-testid="maintenance-form-title" value={form.title} onChange={(e) => handle("title", e.target.value)} placeholder="e.g. Leaking kitchen faucet" />
          </div>
          <div>
            <Label htmlFor="m-desc">Description</Label>
            <Textarea id="m-desc" rows={3} value={form.description} onChange={(e) => handle("description", e.target.value)} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => handle("category", v)}>
                <SelectTrigger data-testid="maintenance-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => handle("priority", v)}>
                <SelectTrigger data-testid="maintenance-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}><span className={p.color}>● {p.label}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="m-est">Estimated cost</Label>
              <Input id="m-est" type="number" step="0.01" value={form.estimated_cost} onChange={(e) => handle("estimated_cost", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="m-actual">Actual cost</Label>
              <Input id="m-actual" type="number" step="0.01" value={form.actual_cost} onChange={(e) => handle("actual_cost", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="m-vendor">Vendor name</Label>
              <Input id="m-vendor" value={form.vendor_name} onChange={(e) => handle("vendor_name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="m-vphone">Vendor phone</Label>
              <Input id="m-vphone" value={form.vendor_phone} onChange={(e) => handle("vendor_phone", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="m-vemail">Vendor email</Label>
              <Input id="m-vemail" type="email" value={form.vendor_email} onChange={(e) => handle("vendor_email", e.target.value)} />
            </div>
          </div>

          <div>
            <Label htmlFor="m-photos">Photos (max 5, JPEG/PNG/WebP, ≤25MB each)</Label>
            <input
              id="m-photos"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={onPickFiles}
              data-testid="maintenance-form-photos"
              className="mt-1 block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            {files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs">
                    <Upload className="h-3 w-3" /> {f.name}
                    <button type="button" onClick={() => setFiles(files.filter((_, ix) => ix !== i))} className="ml-1 text-slate-400 hover:text-red-600">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" className="bg-indigo-600 text-white hover:bg-indigo-700" disabled={saving} data-testid="maintenance-form-submit">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editing ? "Save changes" : "Log work order"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
